import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert, App as AntApp, Button, Card, Flex, Form, Input, InputNumber,
  Modal, Select, Statistic, Switch, Typography,
} from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { API_URL } from '../config';
import { usePerms } from '../hooks/usePerms';
import { getToken } from '../utils/auth';
import { FormLead } from './comercial-leads/FormLead';
import { KanbanLeads } from './comercial-leads/KanbanLeads';
import { ClienteLead, Lead, LeadStatus, ResumoLeads } from './comercial-leads/types';
import { extrairDadosApi, mensagemErroApi } from './comercial-leads/utils';
import './ComercialLeads.css';

const { Title, Text } = Typography;
const RESUMO_VAZIO: ResumoLeads = { ativos: 0, qualificados: 0, atrasados: 0, convertidos_30d: 0 };

function ModalConverterLead({ lead, onClose, onConvertido }: {
  lead: Lead;
  onClose: () => void;
  onConvertido: (negocioId: number) => void;
}) {
  const { message } = AntApp.useApp();
  // Quem originou vira crédito de comissão e o valor estimado vira o valor da
  // venda: os dois pedem a régua de quem gera a venda (06/10/2026, o servidor recusa).
  const { pode, erro: permsErro } = usePerms();
  const podeVenda = permsErro || pode('comercial.negocio_privado.gerar_venda');
  const travadoPelaOriginacao = !podeVenda && !!lead.originador_email;
  const [salvando, setSalvando] = useState(false);
  const [dados, setDados] = useState({
    titulo: lead.interesse,
    area: '',
    valor_estimado: 0,
    observacoes: lead.observacoes || '',
  });

  async function converter() {
    if (!dados.titulo.trim()) { message.warning('Informe o título do negócio.'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const resposta = await axios.post(`${API_URL}/comercial/clientes`, {
        action: 'com_leads_converter',
        id: lead.id,
        titulo: dados.titulo.trim(),
        area: dados.area || null,
        valor_estimado: podeVenda ? (dados.valor_estimado || 0) : 0,
        observacoes: dados.observacoes || null,
        responsavel: lead.responsavel,
        vendedor_email: lead.vendedor_email,
      }, { headers: { Authorization: token } });
      const resultado = extrairDadosApi(resposta);
      message.success('Lead convertido em negócio.');
      onConvertido(Number(resultado.negocio_id));
    } catch (erro: any) {
      message.error(mensagemErroApi(erro));
    } finally { setSalvando(false); }
  }

  return (
    <Modal
      open
      title={`Enviar ${lead.cliente_fantasia || lead.cliente_nome} ao Pipeline`}
      okText="Criar negócio"
      cancelText="Cancelar"
      confirmLoading={salvando}
      okButtonProps={{ disabled: travadoPelaOriginacao }}
      onOk={converter}
      onCancel={onClose}
      width="94vw"
      style={{ top: 16, maxWidth: 620 }}
      styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' } }}
      destroyOnHidden
    >
      <Text type="secondary">
        O negócio começará em “Identificação de necessidades”, mantendo a mesma empresa, contato e responsável.
      </Text>
      {travadoPelaOriginacao && (
        <Alert type="warning" showIcon style={{ marginTop: 12 }}
          message="Este Lead registra quem originou — isso vira crédito de comissão."
          description="Só quem gera a venda (comercial e diretoria) envia este Lead ao Pipeline." />
      )}
      <Form layout="vertical" size="small" style={{ marginTop: 16 }}>
        <Form.Item label="Título do negócio" required>
          <Input value={dados.titulo} onChange={e => setDados(prev => ({ ...prev, titulo: e.target.value }))} />
        </Form.Item>
        <Flex gap={12} wrap="wrap">
          <Form.Item label="Área técnica" style={{ flex: '1 1 220px' }}>
            <Input
              value={dados.area}
              onChange={e => setDados(prev => ({ ...prev, area: e.target.value }))}
              placeholder="Ex.: Hidrologia"
            />
          </Form.Item>
          {podeVenda && (
            <Form.Item label="Valor estimado (R$)" style={{ flex: '1 1 180px' }}>
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={1000}
                value={dados.valor_estimado}
                onChange={valor => setDados(prev => ({ ...prev, valor_estimado: Number(valor || 0) }))}
              />
            </Form.Item>
          )}
        </Flex>
        <Form.Item label="Observações que seguem para o negócio">
          <Input.TextArea
            rows={3}
            value={dados.observacoes}
            onChange={e => setDados(prev => ({ ...prev, observacoes: e.target.value }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function ComercialLeadsInner() {
  const { message, modal } = AntApp.useApp();
  const { pode } = usePerms();
  const podeCriar = pode('comercial.pipeline.criar_negocio_privado');
  const podeEditar = pode('comercial.pipeline.editar_etapa_funil');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [clientes, setClientes] = useState<ClienteLead[]>([]);
  const [resumo, setResumo] = useState<ResumoLeads>(RESUMO_VAZIO);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [responsavel, setResponsavel] = useState('');
  const [mostrarDescartados, setMostrarDescartados] = useState(false);
  const [formAberto, setFormAberto] = useState(false);
  const [leadEditando, setLeadEditando] = useState<Lead | null>(null);
  const [leadConvertendo, setLeadConvertendo] = useState<Lead | null>(null);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const headers = { Authorization: token };
      const [respostaLeads, respostaClientes] = await Promise.all([
        axios.post(`${API_URL}/comercial/clientes`, { action: 'com_leads_listar' }, { headers }),
        axios.get(`${API_URL}/comercial/clientes`, { headers }),
      ]);
      const dadosLeads = extrairDadosApi(respostaLeads) || {};
      setLeads(dadosLeads.items || []);
      setResumo(dadosLeads.resumo || RESUMO_VAZIO);
      setClientes(extrairDadosApi(respostaClientes) || []);
    } catch (erro: any) {
      message.error(`Não foi possível carregar o funil: ${mensagemErroApi(erro)}`);
    } finally { setLoading(false); }
  }

  useEffect(() => { carregar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const responsaveis = useMemo(() => {
    const mapa = new Map<string, string>();
    leads.forEach(lead => {
      if (lead.vendedor_email) mapa.set(lead.vendedor_email, lead.responsavel || lead.vendedor_email);
    });
    return Array.from(mapa, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [leads]);

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLocaleLowerCase('pt-BR');
    return leads.filter(lead => {
      if (!mostrarDescartados && lead.status === 'descartado') return false;
      if (responsavel && lead.vendedor_email !== responsavel) return false;
      if (!termo) return true;
      return [lead.interesse, lead.cliente_nome, lead.cliente_fantasia, lead.contato_nome]
        .some(valor => String(valor || '').toLocaleLowerCase('pt-BR').includes(termo));
    });
  }, [busca, leads, mostrarDescartados, responsavel]);

  async function atualizarEtapa(lead: Lead, status: LeadStatus) {
    const executar = async () => {
      const anterior = leads;
      setLeads(lista => lista.map(item => item.id === lead.id ? { ...item, status } : item));
      try {
        const token = await getToken();
        await axios.post(`${API_URL}/comercial/clientes`, {
          action: 'com_leads_atualizar', id: lead.id, status,
        }, { headers: { Authorization: token } });
        await carregar();
      } catch (erro: any) {
        setLeads(anterior);
        message.error(mensagemErroApi(erro));
      }
    };
    if (status === 'descartado') {
      modal.confirm({
        title: 'Descartar este Lead?',
        content: 'Ele sai do funil ativo, mas permanece disponível em “Mostrar descartados”.',
        okText: 'Descartar',
        cancelText: 'Cancelar',
        okButtonProps: { danger: true },
        onOk: executar,
      });
      return;
    }
    await executar();
  }

  function abrirForm(lead: Lead | null) {
    setLeadEditando(lead);
    setFormAberto(true);
  }

  function concluirConversao(negocioId: number) {
    setLeadConvertendo(null);
    carregar();
    modal.confirm({
      title: 'Negócio criado no Pipeline',
      content: 'O Lead saiu deste funil e entrou em Identificação de necessidades.',
      okText: 'Abrir no Pipeline',
      cancelText: 'Continuar nos Leads',
      onOk: () => {
        sessionStorage.setItem('abrir_proposta_id', String(negocioId));
        window.dispatchEvent(new CustomEvent('app-navigate', {
          detail: { modulo: 'comercial', pagina: 'com-pipeline' },
        }));
      },
    });
  }

  return (
    <div className="comercial-leads">
      <div className="leads-page-header">
        <div>
          <Title level={3} style={{ margin: 0, color: '#020D7F' }}>Funil de Leads</Title>
          <Text type="secondary">Da primeira oportunidade até a entrada no Pipeline de vendas.</Text>
        </div>
        {podeCriar && <Button type="primary" icon={<PlusOutlined />} onClick={() => abrirForm(null)}>Novo Lead</Button>}
      </div>

      <div className="leads-kpis">
        <Card size="small" className="leads-kpi"><Statistic title="Leads ativos" value={resumo.ativos} /></Card>
        <Card size="small" className="leads-kpi"><Statistic title="Qualificados" value={resumo.qualificados} valueStyle={{ color: '#389E0D' }} /></Card>
        <Card size="small" className="leads-kpi"><Statistic title="Contato atrasado" value={resumo.atrasados} valueStyle={{ color: resumo.atrasados ? '#CF1322' : undefined }} /></Card>
        <Card size="small" className="leads-kpi"><Statistic title="Convertidos (30 dias)" value={resumo.convertidos_30d} /></Card>
      </div>

      <div className="leads-toolbar">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Buscar empresa, contato ou oportunidade..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
          style={{ width: 340 }}
        />
        <Select
          allowClear
          placeholder="Todos os responsáveis"
          value={responsavel || undefined}
          options={responsaveis}
          onChange={valor => setResponsavel(valor || '')}
          style={{ width: 220 }}
        />
        <Flex align="center" gap={6}>
          <Switch size="small" checked={mostrarDescartados} onChange={setMostrarDescartados} />
          <Text type="secondary">Mostrar descartados</Text>
        </Flex>
      </div>

      <KanbanLeads
        leads={filtrados}
        loading={loading}
        mostrarDescartados={mostrarDescartados}
        podeEditar={podeEditar}
        podeConverter={podeCriar}
        onEditar={lead => abrirForm(lead)}
        onMudarStatus={atualizarEtapa}
        onConverter={setLeadConvertendo}
      />

      {formAberto && (
        <FormLead
          lead={leadEditando}
          clientes={clientes}
          onClose={() => setFormAberto(false)}
          onSalvo={() => { setFormAberto(false); carregar(); }}
        />
      )}
      {leadConvertendo && (
        <ModalConverterLead
          lead={leadConvertendo}
          onClose={() => setLeadConvertendo(null)}
          onConvertido={concluirConversao}
        />
      )}
    </div>
  );
}

export default function ComercialLeads() {
  return <AntApp><ComercialLeadsInner /></AntApp>;
}
